import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  FileSearch,
  Scale,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface Feature {
  icon: typeof FileSearch;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: FileSearch,
    title: 'Document agent',
    body:
      'Ingests PDF or scanned contracts, extracts text with OCR, and detects language and structure before any reasoning runs.',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance agent',
    body:
      'RAG over your knowledge base of legislation and policies. Cites every clause it flags with the source paragraph and section.',
  },
  {
    icon: Scale,
    title: 'Gate decision',
    body:
      'Deterministic, rule-based gate that aggregates findings into Approve / Approve with conditions / Reject. Same inputs always produce the same decision.',
  },
];

export function WelcomePage() {
  const navigate = useNavigate();
  const { profile, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && profile) {
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, profile, navigate]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:shadow-md focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Background gradient + ambient blobs (CSS-only, no JS) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-background via-background to-primary/5"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-32 -z-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-32 -z-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
      />

      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <Scale className="h-6 w-6 text-primary" aria-hidden="true" />
          <span className="text-lg font-semibold tracking-tight">CMP</span>
        </div>
        <Link
          to="/auth"
          className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Sign in
        </Link>
      </header>

      <main id="main-content" role="main" className="mx-auto max-w-6xl px-6 pb-24 pt-12">
        <section className="mx-auto max-w-3xl text-center">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            Multi-agent contract review
          </p>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Contract review in under 90 seconds.
          </h1>
          <p className="mt-6 text-balance text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Upload a contract. Seven specialised agents extract clauses, check
            compliance, and produce a deterministic gate decision &mdash; with a
            full audit trail you can hand to legal.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/auth"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Sign in to continue
              <ArrowRight
                className="h-4 w-4 transition group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              How it works
            </a>
          </div>
        </section>

        <section
          id="how-it-works"
          aria-labelledby="how-heading"
          className="mt-24 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          <h2 id="how-heading" className="sr-only">
            How CMP works
          </h2>
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="group rounded-xl border border-border bg-card/80 p-6 shadow-sm backdrop-blur transition hover:border-primary/40 hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </article>
          ))}
        </section>

        <footer className="mt-24 border-t border-border pt-8 text-center text-xs text-muted-foreground">
          <p>
            CMP &mdash; Contract Management Platform. A Kaycoo product. GDPR
            compliant. Multi-tenant by design.
          </p>
        </footer>
      </main>
    </div>
  );
}
